import React, { useState } from "react";
import { ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";
import { BRAND } from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-[0.24em] text-black/55">
      {label}
    </span>
    <input
      {...props}
      className="mt-2 w-full bg-transparent border-b border-black/25 focus:border-black outline-none py-3 text-lg placeholder-black/30 transition-colors"
    />
  </label>
);

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    budget: "",
    city: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({
        title: "Missing details",
        description: "Please share at least your name, email and message.",
      });
      return;
    }
    setSending(true);
    // Frontend-only — stores locally, simulates a submit.
    try {
      const list = JSON.parse(localStorage.getItem("ams_leads") || "[]");
      list.unshift({ ...form, at: new Date().toISOString() });
      localStorage.setItem("ams_leads", JSON.stringify(list));
    } catch (storageErr) {
      // Persisting locally is a best-effort UX; log so we can surface storage failures
      // (e.g. private-mode browsers / quota exceeded) without breaking submission.
      console.error("Failed to persist lead locally:", storageErr);
    }
    setTimeout(() => {
      setSending(false);
      toast({
        title: "Brief received",
        description: "Thanks — the AMS team will reach out within 24 hours.",
      });
      setForm({
        name: "",
        company: "",
        email: "",
        phone: "",
        budget: "",
        city: "",
        message: "",
      });
    }, 700);
  };

  return (
    <main>
      <section className="relative bg-black text-white pt-40 pb-20 md:pt-48 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-white/60 mb-6">
            Contact
          </p>
          <h1 className="font-display text-[14vw] md:text-[8vw] uppercase leading-[0.92]">
            Let's <span className="font-serif-display italic font-black">talk.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-white/75 text-lg leading-relaxed">
            Tell us about your brand, your timeline and the markets you want to
            own. We'll come back with a media plan within 24 hours.
          </p>
        </div>
      </section>

      <section className="bg-white text-black">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <p className="text-[11px] uppercase tracking-[0.32em] text-black/55 mb-6">
              Reach us
            </p>
            <h2 className="font-display text-5xl md:text-6xl uppercase leading-[0.92] mb-8">
              Direct lines.
            </h2>
            <ul className="space-y-6">
              <li className="flex items-start gap-4">
                <span className="w-10 h-10 border border-black inline-flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4" />
                </span>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.24em] text-black/55">
                    Email
                  </div>
                  <a
                    href={`mailto:${BRAND.email}`}
                    className="text-lg nav-link break-all"
                  >
                    {BRAND.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-10 h-10 border border-black inline-flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4" />
                </span>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.24em] text-black/55">
                    Phone
                  </div>
                  <div className="flex flex-col">
                    {BRAND.phones.map((p) => (
                      <a
                        key={p}
                        href={`tel:${p.replace(/\s+/g, "")}`}
                        className="text-lg nav-link"
                      >
                        {p}
                      </a>
                    ))}
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-10 h-10 border border-black inline-flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4" />
                </span>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.24em] text-black/55">
                    {BRAND.addressLabel}
                  </div>
                  <span className="text-lg leading-snug block">
                    {BRAND.address}
                  </span>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-10 h-10 border border-black inline-flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4" />
                </span>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.24em] text-black/55">
                    Footprint
                  </div>
                  <span className="text-lg">{BRAND.hq}</span>
                </div>
              </li>
            </ul>
          </div>

          <form
            onSubmit={onSubmit}
            className="lg:col-span-7 order-1 lg:order-2 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8"
          >
            <Field
              label="Your Name"
              name="name"
              value={form.name}
              onChange={onChange}
              placeholder="Full name"
            />
            <Field
              label="Company"
              name="company"
              value={form.company}
              onChange={onChange}
              placeholder="Brand / Agency"
            />
            <Field
              label="Email"
              type="email"
              name="email"
              value={form.email}
              onChange={onChange}
              placeholder="you@brand.com"
            />
            <Field
              label="Phone"
              name="phone"
              value={form.phone}
              onChange={onChange}
              placeholder="+91"
            />
            <Field
              label="Budget (INR)"
              name="budget"
              value={form.budget}
              onChange={onChange}
              placeholder="e.g. 25L – 1 Cr"
            />
            <Field
              label="Target City / Region"
              name="city"
              value={form.city}
              onChange={onChange}
              placeholder="Mumbai, Delhi, Pan-India"
            />

            <label className="block md:col-span-2">
              <span className="text-[11px] uppercase tracking-[0.24em] text-black/55">
                Brief
              </span>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                rows={5}
                placeholder="Tell us about your campaign — goals, timelines, anything we should know."
                className="mt-2 w-full bg-transparent border-b border-black/25 focus:border-black outline-none py-3 text-lg placeholder-black/30 transition-colors resize-none"
              />
            </label>

            <div className="md:col-span-2 flex items-center justify-between flex-wrap gap-4 pt-2">
              <p className="text-xs text-black/55 max-w-md">
                By submitting, you agree AMS may contact you about your enquiry.
                We won't share your details.
              </p>
              <button
                type="submit"
                disabled={sending}
                className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.22em] font-semibold px-7 py-4 border border-black hover:bg-black hover:text-white transition-colors duration-200 disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send Brief"}
                <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Credit line */}
      <section className="bg-white text-black border-t border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-3 text-center md:text-left">
          <p className="text-[11px] uppercase tracking-[0.32em] text-black/55">
            Adways Marketing Solutions
          </p>
          <p className="font-serif-display italic text-base md:text-lg text-black/85">
            Created and visualised by{" "}
            <span className="font-black text-black not-italic font-display uppercase tracking-wider">
              Anhadd Chopra
            </span>
          </p>
        </div>
      </section>
    </main>
  );
};

export default Contact;
